import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { AddL2VPNToAccountDetails } from '@/shared/types/l2vpnTypes';
import { Organization } from '@/shared/types/userTypes';
import { PaginatedResponse } from '@/shared/types/responseTypes';

// Use User Accounts Query
export const useOrganizations = (enabled: boolean, regionCode: string | undefined = undefined) => {
  return useQuery({
    queryKey: ['region-organizations', regionCode],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<Organization>>(
        `/enterprise/v1/organizations`,
        { params: { regionCode } }
      );
      return response.data.results;
    },
    enabled: enabled,
  });
};

/** Paginated accounts list (region-scoped) for Accounts page load-more. */
export const useInfiniteOrganizations = (enabled: boolean, regionCode: string | undefined = undefined) => {
  return useInfiniteQuery({
    queryKey: ['region-organizations-infinite', regionCode],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await apiClient.get<PaginatedResponse<Organization>>(
        `/enterprise/v1/organizations`,
        { params: { regionCode, page: pageParam } }
      );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.isLastPage === false ? lastPageParam + 1 : undefined,
    enabled,
  });
};

// Add L2VPN to Organization Mutation
export const useAddL2VPNCircuitToOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ organizationId, circuits }: { organizationId: string, circuits: AddL2VPNToAccountDetails[] }) => {
      return apiClient.post<APIMessageResponse>(
        `/enterprise/v1/organizations/${organizationId}/l2vpn-circuits`,
        { body: circuits }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['region-l2vpn-circuits-added'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Remove L2VPN from Organization Mutation
export const useRemoveL2VPNCircuitRecordFromOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ circuitRecordId, organizationId }: { circuitRecordId: string; organizationId: string }) => {
      return apiClient.delete<APIMessageResponse>(
        `/enterprise/v1/organizations/${organizationId}/l2vpn-circuits/${circuitRecordId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['region-l2vpn-circuits-removed'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};